<script setup lang="ts">
import { type ShallowRef, computed, ref, shallowRef, useTemplateRef, watch, watchEffect } from 'vue'
import { TabsIndicator, TabsList, TabsRoot } from 'radix-vue'
import { EStates } from '../../composables/useCreateComponentConfig'
import { ELayout, EVariant, type ISlideRefs, type ITabsProps } from './types'
import useTabsConfig from '@/ui/plushComponents/VTabs/composables/useTabsConfig'
import useSliderDimensions from '@/ui/composables/useSliderDimensions'
import useSwipe from '@/ui/composables/useSwipe'
import useCenterActiveTab from '@/ui/composables/useCenterActiveTab'
import { isDark } from '@/lib/utils'

const props = withDefaults(defineProps<ITabsProps>(), {
    variant: EVariant.DEFAULT,
})

const tabsConfig: ShallowRef<Record<string, string>> = shallowRef({} as Record<string, string>)

watchEffect(() => {
    tabsConfig.value = useTabsConfig()
})

// TABS SLIDER
const sliderRef = useTemplateRef('sliderRef')
const slideRefs = ref<ISlideRefs[]>([])
const mutableTranslateX = ref(0) // modified by useSwipe and useCenterActiveTab
const translateX = computed(() => (props.layout === ELayout.HUG ? mutableTranslateX.value : 0))

const { totalWidth, containerWidth } = useSliderDimensions({
    sliderRef,
    slideRefs,
    gap: props.variant === EVariant.PILL ? 8 : 0,
})

const { swipeCooldown } = useSwipe({ sliderRef, totalWidth, containerWidth, mutableTranslateX })

const activeSlide = ref(props.defaultValue)

const { animationEnabled, centerActiveTab, isFocusVisible, checkFocusVisible } = useCenterActiveTab({
    slideRefs,
    activeSlide,
    totalWidth,
    containerWidth,
    swipeCooldown,
    mutableTranslateX,
})

async function handleFocus(): Promise<void> {
    isFocusVisible.value = checkFocusVisible()
}

watch(containerWidth, () => {
    if (props.layout === ELayout.HUG) {
        centerActiveTab(activeSlide.value || 0)
    }
})
</script>

<template>
    <div
        class="max-xl:-mx-6 max-sm:-mx-4"
        :class="[isDark(dark)]"
        data-gpp-tools-ui
    >
        <TabsRoot
            v-model="activeSlide"
            :default-value="defaultValue"
        >
            <!-- Adding padding tab focus on an element with overflow hidden -->
            <div class="overflow-x-hidden pb-1">
                <TabsList
                    :aria-label="listAriaLabel"
                    :class="{
                        'px-1 pt-1': isFocusVisible,
                    }"
                >
                    <div
                        ref="sliderRef"
                        class="max-xl:mx-6 max-sm:mx-4"
                        :class="{
                            'border-neutral-subtlest border-b-2': props.variant === EVariant.DEFAULT,
                            'border-neutral-strong border-b-thin': props.variant === EVariant.PILL,
                        }"
                    >
                        <div
                            class="relative flex w-full"
                            :class="[
                                animationEnabled && !isFocusVisible && 'transition-transform duration-300',
                                variant === EVariant.PILL ? 'gap-xs items-stretch' : 'items-end',
                            ]"
                            :style="{ transform: `translateX(${translateX}px)` }"
                        >
                            <TabsIndicator
                                v-if="variant === EVariant.DEFAULT"
                                :class="tabsConfig.indicatorClasses"
                            />
                            <VTabsTrigger
                                v-for="(tab, index) in tabsList"
                                :ref="el => (slideRefs[index] = el as ISlideRefs)"
                                :key="tab.value"
                                :name="tab.name"
                                :value="tab.value"
                                :size="size"
                                :layout="layout"
                                :variant="variant"
                                :disabled="tab.disabled"
                                :icon="tab.icon"
                                :state="tab.value === activeSlide ? EStates.SELECTED : undefined"
                                @click.prevent="centerActiveTab(tab.value)"
                                @focus="handleFocus"
                            />
                        </div>
                    </div>
                </TabsList>
            </div>

            <div class="max-xl:mx-6 max-sm:mx-4">
                <slot :active-slide="activeSlide">
                    <VTabsAnimation>
                        <VTabsContent
                            v-for="tab in tabsList?.filter(tab => activeSlide === tab.value)"
                            :key="tab.value"
                            :value="tab.value"
                            :content="tab.content"
                            :class="contentClass"
                        />
                    </VTabsAnimation>
                </slot>
            </div>
        </TabsRoot>
    </div>
</template>
